/* KdvSwitcher — badge + quick-switch dropdown voor multi-KDV directeurs.
 *
 * Toont de actieve KDV rechtsboven met de tenant-kleur als accent. Als er
 * meerdere KDVs achter dezelfde e-mail hangen wordt de badge een dropdown-
 * trigger waarmee je zonder uitloggen naar een andere vestiging switcht.
 *
 * Endpoint: GET /api/auth/my-kdvs · POST /api/auth/switch-kdv
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Building2, Check, ChevronDown, Loader2 } from "lucide-react";

/**
 * @param {{ activeTenant: {id?:string,name?:string,kleur_primair?:string}|null,
 *           onSwitched: () => void }} props
 */
export default function KdvSwitcher({ activeTenant, onSwitched }) {
  const [kdvs, setKdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/auth/my-kdvs");
        if (mounted) setKdvs(r.data?.items || []);
      } catch {
        if (mounted) setKdvs([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [activeTenant?.id]);

  const others = kdvs.filter(k => !k.is_current);
  const current = kdvs.find(k => k.is_current);
  // Kleur uit backend, fallback naar sage-theme kleur
  const color = current?.kleur_primair || activeTenant?.kleur_primair || "#2D4A3E";
  const label = activeTenant?.name || current?.organization_name || "—";

  const doSwitch = async (userId) => {
    setSwitching(userId);
    try {
      await api.post("/auth/switch-kdv", { user_id: userId });
      toast.success("Gewisseld van KDV — pagina herlaadt");
      // Volledige reload zodat álle context (auth, tenant-branding, notifs) opnieuw wordt geladen
      setTimeout(() => window.location.reload(), 300);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setSwitching(null);
    }
  };

  // Zonder andere KDVs: gewoon een statische badge tonen (geen dropdown)
  if (loading) {
    return (
      <div data-testid="kdv-badge-loading"
           className="hidden lg:flex items-center gap-2 px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-500 text-xs">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>KDV laden…</span>
      </div>
    );
  }

  if (others.length === 0) {
    return (
      <div data-testid="kdv-badge"
           className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium max-w-[240px]"
           style={{ borderColor: color, color, background: `${color}12` }}>
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="kdv-switcher-trigger"
          className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium max-w-[280px] hover:brightness-95 transition"
          style={{ borderColor: color, color, background: `${color}12` }}
          aria-label={`Actieve KDV: ${label}. Klik om te wisselen.`}>
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
          <Building2 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 bg-white" data-testid="kdv-switcher-menu">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-neutral-500">
          Wissel van KDV
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {kdvs.map((k) => (
          <DropdownMenuItem
            key={k.user_id}
            data-testid={`kdv-switch-item-${k.organization_slug || k.user_id}`}
            disabled={k.is_current || switching !== null}
            onClick={() => !k.is_current && doSwitch(k.user_id)}
            className="flex items-center gap-2 py-2.5 cursor-pointer">
            <span className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: k.kleur_primair || "#2D4A3E" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-neutral-800 truncate">
                {k.organization_name}
              </div>
              <div className="text-[11px] text-neutral-500 truncate">
                {k.role === "directeur" || k.role === "superadmin" ? "Directeur" : k.role}
              </div>
            </div>
            {k.is_current && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
            {switching === k.user_id && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
